import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const LOGIN_RATE_LIMIT_KEY = 'cab-store-login-rate-limit';
const LOGIN_ATTEMPT_LIMIT = 5;
const LOGIN_COOLDOWN_MS = 60_000;

const readLoginRateLimit = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(LOGIN_RATE_LIMIT_KEY)) || {};
    return {
      failedAttempts: Number.isInteger(stored.failedAttempts) && stored.failedAttempts > 0 ? stored.failedAttempts : 0,
      blockedUntil: Number.isFinite(stored.blockedUntil) ? stored.blockedUntil : 0,
    };
  } catch {
    return { failedAttempts: 0, blockedUntil: 0 };
  }
};

const writeLoginRateLimit = (rateLimit) => {
  try {
    window.localStorage.setItem(LOGIN_RATE_LIMIT_KEY, JSON.stringify(rateLimit));
  } catch {
    // Login should remain available when browser storage is disabled.
  }
};

export const customerSupabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_ANON_KEY, {
  auth: {
    storageKey: 'cab-store-customer-auth',
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginCooldownSeconds, setLoginCooldownSeconds] = useState(0);
  const loginRequestInProgress = useRef(false);

  useEffect(() => {
    const updateCooldown = () => {
      const { blockedUntil = 0 } = readLoginRateLimit();
      setLoginCooldownSeconds(Math.max(0, Math.ceil((blockedUntil - Date.now()) / 1000)));
    };
    updateCooldown();
    const interval = window.setInterval(updateCooldown, 1000);
    window.addEventListener('storage', updateCooldown);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('storage', updateCooldown);
    };
  }, []);

  const loadCustomer = async (session) => {
    const nextUser = session?.user || null;
    if (!nextUser) {
      setUser(null);
      setProfile(null);
      return;
    }

    const { data, error: profileError } = await customerSupabase
      .from('users')
      .select('role, name, phone, address')
      .eq('id', nextUser.id)
      .maybeSingle();
    if (profileError || data?.role === 'admin') {
      await customerSupabase.auth.signOut();
      setUser(null);
      setProfile(null);
      return;
    }
    setUser(nextUser);
    setProfile(data || {});
  };

  useEffect(() => {
    let mounted = true;
    customerSupabase.auth.getSession()
      .then(({ data: { session } }) => loadCustomer(session))
      .finally(() => { if (mounted) setLoading(false); });
    const { data: listener } = customerSupabase.auth.onAuthStateChange((_event, session) => {
      window.setTimeout(() => {
        loadCustomer(session).finally(() => { if (mounted) setLoading(false); });
      }, 0);
    });
    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    if (loginRequestInProgress.current) return { success: false };

    const rateLimit = readLoginRateLimit();
    const remainingSeconds = Math.max(0, Math.ceil(((rateLimit.blockedUntil || 0) - Date.now()) / 1000));
    if (remainingSeconds > 0) {
      setLoginCooldownSeconds(remainingSeconds);
      setError(`Trop de tentatives. Réessayez dans ${remainingSeconds} secondes.`);
      return { success: false };
    }

    loginRequestInProgress.current = true;
    setActionLoading(true);
    setError('');
    try {
      const { data, error: loginError } = await customerSupabase.auth.signInWithPassword({ email, password });
      if (loginError) {
        const failedAttempts = rateLimit.failedAttempts + 1;
        if (failedAttempts >= LOGIN_ATTEMPT_LIMIT) {
          const blockedUntil = Date.now() + LOGIN_COOLDOWN_MS;
          writeLoginRateLimit({ failedAttempts: 0, blockedUntil });
          setLoginCooldownSeconds(Math.ceil(LOGIN_COOLDOWN_MS / 1000));
          setError('Trop de tentatives. Réessayez dans 60 secondes.');
        } else {
          writeLoginRateLimit({ failedAttempts, blockedUntil: 0 });
          setError('Email ou mot de passe incorrect.');
        }
        return { success: false };
      }
      writeLoginRateLimit({ failedAttempts: 0, blockedUntil: 0 });
      const { data: account, error: roleError } = await customerSupabase.from('users').select('role').eq('id', data.user.id).maybeSingle();
      if (roleError || account?.role === 'admin') {
        await customerSupabase.auth.signOut();
        setError('Ce compte n’est pas un compte client.');
        return { success: false };
      }
      await loadCustomer({ user: data.user });
      return { success: true };
    } catch {
      setError('Connexion impossible. Vérifiez votre connexion et réessayez.');
      return { success: false };
    } finally {
      setActionLoading(false);
      loginRequestInProgress.current = false;
    }
  };

  const register = async ({ name, email, password, phone }) => {
    setActionLoading(true);
    setError('');
    const { data, error: registerError } = await customerSupabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: name, phone }, emailRedirectTo: window.location.origin },
    });
    if (registerError) {
      setError(registerError.message === 'User already registered' ? 'Cette adresse e-mail possède déjà un compte.' : registerError.message);
      setActionLoading(false);
      return { success: false };
    }
    if (data.session) {
      await customerSupabase.from('users').upsert({ id: data.user.id, email, name, phone, role: 'user' });
      await loadCustomer(data);
    }
    let welcomeEmailFailed = false;
    try {
      const storeUrl = process.env.REACT_APP_STORE_URL || window.location.origin;
      await customerSupabase.functions.invoke('send-welcome-email', {
        body: {
          email,
          customerName: name,
          firstName: name.split(/\s+/).filter(Boolean)[0] || '',
          storeUrl,
        },
      });
    } catch (emailError) {
      console.warn('CAB STORE welcome email notification could not be sent.', emailError);
      welcomeEmailFailed = true;
    }
    setActionLoading(false);
    return { success: true, requiresConfirmation: !data.session, welcomeEmailFailed };
  };

  const updateProfile = async (values) => {
    if (!user) return { success: false };
    setActionLoading(true);
    setError('');
    const { error: updateError } = await customerSupabase.from('users').update({ name: values.name, phone: values.phone, address: values.address }).eq('id', user.id);
    if (updateError) setError(updateError.message);
    else setProfile((current) => ({ ...current, ...values }));
    setActionLoading(false);
    return { success: !updateError };
  };

  const logout = async () => {
    await customerSupabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  return <AuthContext.Provider value={{ user, profile, loading, actionLoading, error, login, loginCooldownSeconds, register, updateProfile, logout, setError }}>{children}</AuthContext.Provider>;
};